// PinLock — pantalla simple para ingresar PIN de 4 dígitos.
// Modos:
//   "set"     → crear PIN (registra también en servidor si user está auth)
//   "verify"  → verificar PIN. Si hay device_key local, va al servidor
//               y obtiene un session_token nuevo sin pasar por Google.
//               Si no hay device_key, hace verify local (fallback).
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Delete } from "lucide-react";
import { setPin, verifyPin, unlockPin, setDeviceKey, getDeviceKey, removePin } from "../auth/pinStore";
import { useAuth } from "../auth/AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PinLock({ mode = "verify", onSuccess, onSkip, hint }) {
  const { setUser } = useAuth();
  const [pin, setPinState] = useState("");
  const [confirm, setConfirm] = useState("");
  const [stage, setStage] = useState("enter"); // 'enter' | 'confirm'
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const handleDigit = (d) => {
    if (busy) return;
    setError("");
    if (stage === "enter") {
      if (pin.length >= 4) return;
      setPinState(pin + d);
    } else {
      if (confirm.length >= 4) return;
      setConfirm(confirm + d);
    }
  };

  const handleDelete = () => {
    if (busy) return;
    setError("");
    if (stage === "enter") setPinState(pin.slice(0, -1));
    else setConfirm(confirm.slice(0, -1));
  };

  // Auto-process when 4 digits entered
  useEffect(() => {
    (async () => {
      if (busy) return;
      if (mode === "set") {
        if (stage === "enter" && pin.length === 4) {
          setTimeout(() => setStage("confirm"), 200);
        } else if (stage === "confirm" && confirm.length === 4) {
          if (pin !== confirm) {
            setError("Los PIN no coinciden, intenta de nuevo");
            setTimeout(() => { setPinState(""); setConfirm(""); setStage("enter"); }, 800);
            return;
          }
          // Save PIN locally
          await setPin(pin);
          unlockPin();
          // Register on server too (so this device can re-authenticate via PIN later
          // without needing Google again). Requires user to be authenticated.
          setBusy(true);
          try {
            const tok = localStorage.getItem("session_token");
            const r = await fetch(`${API}/api/auth/pin/register`, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
                ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
              },
              body: JSON.stringify({ pin }),
            });
            if (r.ok) {
              const data = await r.json();
              if (data.device_key) setDeviceKey(data.device_key);
            }
            // If register fails (user not authenticated), no problem —
            // it'll be auto-registered later from MyTrips once auth is good.
          } catch { /* offline, will retry later */ }
          setBusy(false);
          onSuccess?.();
        }
      } else if (mode === "verify") {
        if (pin.length !== 4) return;
        setBusy(true);

        // Path 1: server-side PIN auth (preferred, doesn't need Google)
        const deviceKey = getDeviceKey();
        if (deviceKey) {
          try {
            const r = await fetch(`${API}/api/auth/pin/login`, {
              method: "POST",
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ device_key: deviceKey, pin }),
            });
            if (r.ok) {
              const data = await r.json();
              if (data.session_token) {
                try { localStorage.setItem("session_token", data.session_token); } catch {}
              }
              setUser?.({ user_id: data.user_id, email: data.email, name: data.name, picture: data.picture });
              unlockPin();
              setBusy(false);
              onSuccess?.({ authed: true });
              return;
            } else if (r.status === 401) {
              setError("PIN incorrecto");
              setTimeout(() => setPinState(""), 600);
              setBusy(false);
              return;
            }
          } catch {
            // Network error → fall through to local verify (offline mode)
          }
        }

        // Path 2: legacy/offline local verify
        const ok = await verifyPin(pin);
        if (ok) {
          unlockPin();
          setBusy(false);
          // Caller decides if extra steps needed (e.g. force Google to enroll device)
          onSuccess?.({ authed: false });
        } else {
          setError("PIN incorrecto");
          setTimeout(() => setPinState(""), 600);
          setBusy(false);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, confirm, stage, mode]);

  const currentValue = stage === "enter" ? pin : confirm;
  const titleByMode = mode === "set"
    ? (stage === "enter" ? "Crea tu PIN de 4 dígitos" : "Confírmalo")
    : "Ingresa tu PIN";

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-10"
      style={{ background: "linear-gradient(180deg, hsl(35 38% 96%) 0%, hsl(35 30% 92%) 100%)" }}
      data-testid="pin-lock-page"
    >
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-sm w-full">
        <div className="text-center mb-8">
          <div className="text-5xl mb-2">🔐</div>
          <h2 className="text-xl font-bold leading-tight">{titleByMode}</h2>
          {hint && <p className="text-xs text-[hsl(var(--muted))] mt-2">{hint}</p>}
          {error && <p className="text-xs text-red-600 mt-3 font-medium" data-testid="pin-error">{error}</p>}
          {busy && <p className="text-xs text-[hsl(var(--muted))] mt-2 italic">Verificando…</p>}
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-3 mb-8" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full transition-all ${
                i < currentValue.length ? "bg-[hsl(var(--ink))]" : "bg-[hsl(var(--line))]"
              }`}
            />
          ))}
        </div>

        {/* Keypad */}
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => (
            <button
              key={n}
              onClick={() => handleDigit(String(n))}
              className="aspect-square rounded-full bg-white shadow-sm border border-[hsl(var(--line))] text-2xl font-semibold active:scale-95 transition-transform disabled:opacity-50"
              data-testid={`pin-key-${n}`}
              disabled={busy}
            >
              {n}
            </button>
          ))}
          <div />
          <button
            onClick={() => handleDigit("0")}
            className="aspect-square rounded-full bg-white shadow-sm border border-[hsl(var(--line))] text-2xl font-semibold active:scale-95 transition-transform disabled:opacity-50"
            data-testid="pin-key-0"
            disabled={busy}
          >
            0
          </button>
          <button
            onClick={handleDelete}
            className="aspect-square rounded-full flex items-center justify-center text-[hsl(var(--muted))] active:scale-95 transition-transform disabled:opacity-50"
            data-testid="pin-key-delete"
            aria-label="Borrar"
            disabled={busy}
          >
            <Delete size={22} />
          </button>
        </div>

        {/* Skip option (during set) */}
        {mode === "set" && onSkip && (
          <button onClick={onSkip} className="block mx-auto mt-6 text-xs text-[hsl(var(--muted))] underline" data-testid="pin-skip">
            Configurar más tarde
          </button>
        )}
      </motion.div>
    </div>
  );
}
